import { Rectangle } from 'pixi.js';
import { assertNumber, assertMinimum, assertIsValidAlpha } from '../../validators';
import type { StencilMaskSettings } from './StencilMask';
import { StencilMask } from './StencilMask';

export class CircleMask extends StencilMask {
    override _classType = 'CircleMask';

    #fillAlpha: number = 1;
    #lineThickness: number = 0;
    #startRadius: number = 0;
    #endRadius: number = 0.5;
    #centerX: number = 0.5;
    #centerY: number = 0.5;

    #morphStartRadius: number | null = null;
    #morphEndRadius: number | null = null;
    #morphCenterX: number | null = null;
    #morphCenterY: number | null = null;

    constructor(settings: StencilMaskSettings) {
        super(settings);

        const resolution = this.root.referenceResolution;

        this.#centerX = resolution.width * 0.5;
        this.#centerY = resolution.height * 0.5;
    }

    get fillAlpha(): number {
        return this.#fillAlpha;
    }

    set fillAlpha(value: number) {
        if (this.fillAlpha === value) {
            return;
        }
        assertIsValidAlpha(value);
        this.#fillAlpha = value;
        this.requiresUpdate = true;
    }

    get lineThickness(): number {
        return this.#lineThickness;
    }

    set lineThickness(value: number) {
        if (this.lineThickness === value) {
            return;
        }
        assertNumber(value);
        this.#lineThickness = value;
        this.requiresUpdate = true;
    }

    get startRadius(): number {
        return this.#startRadius;
    }

    set startRadius(value: number) {
        if (this.startRadius === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#startRadius = value;
        this.requiresUpdate = true;
    }

    get endRadius(): number {
        return this.#endRadius;
    }

    set endRadius(value: number) {
        if (this.#endRadius === value) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#endRadius = value;
        this.requiresUpdate = true;
    }

    set centerX(value: number) {
        if (this.#centerX === value) {
            return;
        }
        assertNumber(value);
        this.#centerX = value;
        this.requiresUpdate = true;
    }

    get centerX(): number {
        return this.#centerX;
    }

    set centerY(value: number) {
        if (this.#centerY === value) {
            return;
        }
        assertNumber(value);
        this.#centerY = value;
        this.requiresUpdate = true;
    }

    get centerY(): number {
        return this.#centerY;
    }

    override getMaxBounds(): Rectangle {
        const startRadius = this.#morphStartRadius ?? this.#startRadius;
        const endRadius = this.#morphEndRadius ?? this.#endRadius;
        const centerX = this.#morphCenterX ?? this.#centerX;
        const centerY = this.#morphCenterY ?? this.#centerY;
        const maxRadius = Math.max(startRadius, endRadius);
        return new Rectangle(
            centerX - maxRadius,
            centerY - maxRadius,
            maxRadius * 2,
            maxRadius * 2,
        );
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'centerX',
            'centerY',
            'startRadius',
            'endRadius',
            'lineThickness',
            'fillAlpha',
        ]);
    }

    override _update(): void {
        const startRadius = this.#morphStartRadius ?? this.#startRadius;
        const endRadius = this.#morphEndRadius ?? this.#endRadius;
        const centerX = this.#morphCenterX ?? this.#centerX;
        const centerY = this.#morphCenterY ?? this.#centerY;

        const radiusTransitionSize = endRadius - startRadius;
        const radius = startRadius + radiusTransitionSize * this.transition;

        this.clear();
        this.lineStyle(this.#lineThickness, 0xffffff, 1);
        this.beginFill(0xffffff, this.#fillAlpha);
        this.drawCircle(centerX, centerY, radius);
        this.endFill();
    }

    /**
     * @internal
     */
    _setMorphState(state: {
        startRadius: number;
        endRadius: number;
        centerX: number;
        centerY: number;
    }): void {
        this.#morphStartRadius = state.startRadius;
        this.#morphEndRadius = state.endRadius;
        this.#morphCenterX = state.centerX;
        this.#morphCenterY = state.centerY;
        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    _clearMorphState(): void {
        this.#morphStartRadius = null;
        this.#morphEndRadius = null;
        this.#morphCenterX = null;
        this.#morphCenterY = null;
        this.requiresUpdate = true;
    }
}
